/**
 * Pair two structures' line items and mark rows that differ.
 * Timber identity uses the same FIFO `lineKey(name, dims)` as CSV compare.
 */

import type {
  DeckingItem,
  DesignNodeData,
  HardwareListItem,
  InstallEquipmentLine,
  InstallLaborLine,
  InstallNodeData,
  PostBasesItem,
  ProposalNodeSet,
  TimberListItem,
  TravelNodeData,
} from '../types';
import { lineKey } from './proposalTimberDiff';
import { getStructureDeckingItems } from './proposalStructureDecking';
import { getStructurePostBasesItems } from './proposalStructurePostBases';
import { getInstallLaborLines } from './proposalInstallLabor';
import { travelHotelRooms } from './proposalTravelTotals';

export type StructureLineCompareMark = 'changed' | 'onlyThis';

export interface StructureLineFieldDiff {
  field: string;
  thisValue: string | number;
  otherValue: string | number;
}

export interface StructureLineCompareEntry {
  mark: StructureLineCompareMark;
  diffs: StructureLineFieldDiff[];
}

export type StructureLineCompareMap = Record<string, StructureLineCompareEntry>;

export type StructureCompareSession = {
  leftNodeSetId: string;
  right:
    | { source: 'local'; nodeSetId: string }
    | { source: 'remote'; dealId: string; dealTitle: string; nodeSet: ProposalNodeSet };
};

export interface StructureLineCompareResult {
  timber: StructureLineCompareMap;
  hardware: StructureLineCompareMap;
  decking: StructureLineCompareMap;
  postBases: StructureLineCompareMap;
  /** Fixed rows (joinery hours, travel miles, install days) plus labor/equip by this-side id. */
  fields: StructureLineCompareMap;
}

const EMPTY_COMPARE: StructureLineCompareResult = {
  timber: {},
  hardware: {},
  decking: {},
  postBases: {},
  fields: {},
};

function normText(value: string | undefined | null): string {
  return (value || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function numsEqual(a: number, b: number): boolean {
  return Math.abs((Number(a) || 0) - (Number(b) || 0)) < 0.001;
}

function onlyThisMap<T extends { id: string }>(items: T[]): StructureLineCompareMap {
  const marks: StructureLineCompareMap = {};
  for (const item of items) {
    if (!item.id) continue;
    marks[item.id] = { mark: 'onlyThis', diffs: [] };
  }
  return marks;
}

function pairByKey<T extends { id: string }>(
  thisItems: T[],
  otherItems: T[],
  keyOf: (item: T) => string,
  fieldDiffs: (a: T, b: T) => StructureLineFieldDiff[],
): StructureLineCompareMap {
  const buckets = new Map<string, T[]>();
  for (const item of otherItems) {
    const key = keyOf(item);
    const arr = buckets.get(key) ?? [];
    arr.push(item);
    buckets.set(key, arr);
  }
  const consumed = new Set<string>();
  const marks: StructureLineCompareMap = {};

  for (const item of thisItems) {
    if (!item.id) continue;
    const key = keyOf(item);
    const bucket = buckets.get(key);
    const match = bucket?.find(other => !consumed.has(other.id));
    if (!match) {
      marks[item.id] = { mark: 'onlyThis', diffs: [] };
      continue;
    }
    consumed.add(match.id);
    const diffs = fieldDiffs(item, match);
    if (diffs.length > 0) {
      marks[item.id] = { mark: 'changed', diffs };
    }
  }
  return marks;
}

export function timberListForSpeciesTab(
  nodeSet: ProposalNodeSet | null | undefined,
  tab: string,
): TimberListItem[] | null {
  if (!nodeSet) return null;
  if (tab === 'default') return nodeSet.materials.timberList || [];
  const alt = (nodeSet.materials.alternateSpecies || []).find(
    species => species.speciesName === tab,
  );
  return alt ? alt.timberList || [] : null;
}

function timberFieldDiffs(a: TimberListItem, b: TimberListItem): StructureLineFieldDiff[] {
  const diffs: StructureLineFieldDiff[] = [];
  if (!numsEqual(a.quantity, b.quantity)) {
    diffs.push({ field: 'qty', thisValue: a.quantity, otherValue: b.quantity });
  }
  if (normText(a.species) !== normText(b.species)) {
    diffs.push({ field: 'species', thisValue: a.species || '—', otherValue: b.species || '—' });
  }
  if (normText(a.grade) !== normText(b.grade)) {
    diffs.push({ field: 'grade', thisValue: a.grade || '—', otherValue: b.grade || '—' });
  }
  if (normText(a.moistureContent) !== normText(b.moistureContent)) {
    diffs.push({
      field: 'dry',
      thisValue: a.moistureContent || '—',
      otherValue: b.moistureContent || '—',
    });
  }
  if (!numsEqual(a.unitCost, b.unitCost)) {
    diffs.push({
      field: '$/ea',
      thisValue: Math.round(a.unitCost),
      otherValue: Math.round(b.unitCost),
    });
  }
  return diffs;
}

export function compareTimberLists(
  thisList: TimberListItem[] | null,
  otherList: TimberListItem[] | null,
): StructureLineCompareMap {
  if (!thisList?.length) return {};
  if (!otherList) return onlyThisMap(thisList);
  return pairByKey(thisList, otherList, item => lineKey(item.name, item.dimensions), timberFieldDiffs);
}

export function hardwareMatchKey(item: HardwareListItem): string {
  return [
    item.group || '',
    normText(item.name),
    normText(item.material || item.section),
    normText(item.length).replace(/\s+/g, ''),
  ].join('|');
}

function hardwareFieldDiffs(a: HardwareListItem, b: HardwareListItem): StructureLineFieldDiff[] {
  const diffs: StructureLineFieldDiff[] = [];
  if (!numsEqual(a.quantity, b.quantity)) {
    diffs.push({ field: 'qty', thisValue: a.quantity, otherValue: b.quantity });
  }
  const thisCost = Number(a.unitCost) || Number(a.total) || 0;
  const otherCost = Number(b.unitCost) || Number(b.total) || 0;
  if (!numsEqual(thisCost, otherCost)) {
    diffs.push({
      field: 'cost',
      thisValue: Math.round(thisCost),
      otherValue: Math.round(otherCost),
    });
  }
  return diffs;
}

export function compareHardwareLists(
  thisList: HardwareListItem[] | undefined | null,
  otherList: HardwareListItem[] | undefined | null,
): StructureLineCompareMap {
  const left = thisList || [];
  const right = otherList || [];
  if (!left.length) return {};
  return pairByKey(left, right, hardwareMatchKey, hardwareFieldDiffs);
}

function deckingMatchKey(item: DeckingItem): string {
  return `${normText(item.productName)}|${Number(item.widthInches) || 0}`;
}

function deckingFieldDiffs(a: DeckingItem, b: DeckingItem): StructureLineFieldDiff[] {
  const diffs: StructureLineFieldDiff[] = [];
  if (!numsEqual(a.sqFt, b.sqFt)) {
    diffs.push({ field: 'sq ft', thisValue: a.sqFt, otherValue: b.sqFt });
  }
  if (!numsEqual(a.pricePerLinFt, b.pricePerLinFt)) {
    diffs.push({
      field: '$/lf',
      thisValue: a.pricePerLinFt,
      otherValue: b.pricePerLinFt,
    });
  }
  return diffs;
}

export function compareDeckingItems(
  thisList: DeckingItem[],
  otherList: DeckingItem[],
): StructureLineCompareMap {
  if (!thisList.length) return {};
  return pairByKey(thisList, otherList, deckingMatchKey, deckingFieldDiffs);
}

function postBasesMatchKey(item: PostBasesItem): string {
  return normText(item.productName);
}

function postBasesFieldDiffs(a: PostBasesItem, b: PostBasesItem): StructureLineFieldDiff[] {
  const diffs: StructureLineFieldDiff[] = [];
  if (!numsEqual(a.quantity, b.quantity)) {
    diffs.push({ field: 'qty', thisValue: a.quantity, otherValue: b.quantity });
  }
  if (!numsEqual(a.unitCost, b.unitCost)) {
    diffs.push({ field: 'cost', thisValue: a.unitCost, otherValue: b.unitCost });
  }
  return diffs;
}

export function comparePostBasesItems(
  thisList: PostBasesItem[],
  otherList: PostBasesItem[],
): StructureLineCompareMap {
  if (!thisList.length) return {};
  return pairByKey(thisList, otherList, postBasesMatchKey, postBasesFieldDiffs);
}

function addField(
  marks: StructureLineCompareMap,
  key: string,
  diffs: StructureLineFieldDiff[],
) {
  if (diffs.length) marks[key] = { mark: 'changed', diffs };
}

function numDiff(
  field: string,
  thisValue: number | undefined | null,
  otherValue: number | undefined | null,
): StructureLineFieldDiff | null {
  const a = Number(thisValue) || 0;
  const b = Number(otherValue) || 0;
  if (numsEqual(a, b)) return null;
  return { field, thisValue: a, otherValue: b };
}

function compactDiffs(list: Array<StructureLineFieldDiff | null>): StructureLineFieldDiff[] {
  return list.filter((d): d is StructureLineFieldDiff => !!d);
}

function laborMatchKey(line: InstallLaborLine): string {
  return line.employeeUserId || normText(line.role) || line.id;
}

function laborFieldDiffs(a: InstallLaborLine, b: InstallLaborLine): StructureLineFieldDiff[] {
  return compactDiffs([
    numDiff('people', a.quantity, b.quantity),
    numDiff('$/day', a.ratePerDay, b.ratePerDay),
  ]);
}

function equipmentMatchKey(line: InstallEquipmentLine): string {
  return `${line.kind}|${line.overtime ? 'ot' : line.period}`;
}

function equipmentFieldDiffs(a: InstallEquipmentLine, b: InstallEquipmentLine): StructureLineFieldDiff[] {
  return compactDiffs([
    numDiff('machines', a.units ?? 1, b.units ?? 1),
    numDiff('qty', a.quantity, b.quantity),
    numDiff('rate', a.rate, b.rate),
  ]);
}

export function compareDesignFields(a: DesignNodeData, b: DesignNodeData): StructureLineCompareMap {
  const marks: StructureLineCompareMap = {};
  addField(marks, 'design.pm', compactDiffs([
    numDiff('hours', a.pmHours, b.pmHours),
    numDiff('$/hr', a.pmRate, b.pmRate),
  ]));
  addField(marks, 'design.apm', compactDiffs([
    numDiff('hours', a.apmHours, b.apmHours),
    numDiff('$/hr', a.apmRate, b.apmRate),
  ]));
  addField(marks, 'design.lead', compactDiffs([
    numDiff('hours', a.leadDesignerHours, b.leadDesignerHours),
    numDiff('$/hr', a.leadDesignerRate, b.leadDesignerRate),
  ]));
  addField(marks, 'design.support', compactDiffs([
    numDiff('hours', a.supportDesignerHours, b.supportDesignerHours),
    numDiff('$/hr', a.supportDesignerRate, b.supportDesignerRate),
  ]));
  addField(marks, 'design.engineering', compactDiffs([
    numDiff('hours', a.engineeringHours, b.engineeringHours),
    numDiff('lump', a.engineeringLumpSum, b.engineeringLumpSum),
    (a.engineeringMode || 'hourly') !== (b.engineeringMode || 'hourly')
      ? {
          field: 'mode',
          thisValue: a.engineeringMode || 'hourly',
          otherValue: b.engineeringMode || 'hourly',
        }
      : null,
  ]));
  return marks;
}

export function compareInstallFields(a: InstallNodeData, b: InstallNodeData): StructureLineCompareMap {
  const marks: StructureLineCompareMap = {
    ...pairByKey(getInstallLaborLines(a), getInstallLaborLines(b), laborMatchKey, laborFieldDiffs),
    ...pairByKey(a.equipmentLines || [], b.equipmentLines || [], equipmentMatchKey, equipmentFieldDiffs),
  };
  addField(marks, 'install.days', compactDiffs([numDiff('days', a.days, b.days)]));
  addField(marks, 'install.fuel', compactDiffs([numDiff('cost', a.fuelBudget, b.fuelBudget)]));
  const kinds = ['crane', 'telehandler', 'scissorLift', 'basketLift'] as const;
  for (const kind of kinds) {
    const thisOn = kind === 'crane' ? a.craneRequired
      : kind === 'telehandler' ? a.telehandlerRequired
      : kind === 'scissorLift' ? a.scissorLiftRequired
      : !!a.basketLiftRequired;
    const otherOn = kind === 'crane' ? b.craneRequired
      : kind === 'telehandler' ? b.telehandlerRequired
      : kind === 'scissorLift' ? b.scissorLiftRequired
      : !!b.basketLiftRequired;
    if (thisOn !== otherOn) {
      marks[`install.equip.${kind}`] = {
        mark: thisOn && !otherOn ? 'onlyThis' : 'changed',
        diffs: [{ field: 'on', thisValue: thisOn ? 'yes' : 'no', otherValue: otherOn ? 'yes' : 'no' }],
      };
    }
  }
  return marks;
}

export function compareTravelFields(a: TravelNodeData, b: TravelNodeData): StructureLineCompareMap {
  const marks: StructureLineCompareMap = {};
  addField(marks, 'travel.miles', compactDiffs([numDiff('miles', a.milesToJobSite, b.milesToJobSite)]));
  addField(marks, 'travel.fuel', compactDiffs([numDiff('cost', a.fuelBudget, b.fuelBudget)]));
  addField(marks, 'travel.days', compactDiffs([numDiff('days', a.travelDays, b.travelDays)]));
  addField(marks, 'travel.hotel', compactDiffs([
    numDiff('rooms', travelHotelRooms(a), travelHotelRooms(b)),
    numDiff('nights', a.hotelNights, b.hotelNights),
    numDiff('rate', a.hotelRatePerNight, b.hotelRatePerNight),
  ]));
  addField(marks, 'travel.crew', compactDiffs([numDiff('people', a.crewSize, b.crewSize)]));
  addField(marks, 'travel.perDiem', compactDiffs([
    numDiff('$/person', a.perDiemPerPersonPerDay, b.perDiemPerPersonPerDay),
  ]));
  return marks;
}

/** Marks for this structure vs the other, using this pane's visible species tab. */
export function compareStructureLineItems(opts: {
  thisNodeSet: ProposalNodeSet | null | undefined;
  otherNodeSet: ProposalNodeSet | null | undefined;
  thisSpeciesTab: string;
}): StructureLineCompareResult {
  const { thisNodeSet, otherNodeSet, thisSpeciesTab } = opts;
  if (!thisNodeSet || !otherNodeSet) return EMPTY_COMPARE;

  const thisTimber = timberListForSpeciesTab(thisNodeSet, thisSpeciesTab);
  const otherTimber = timberListForSpeciesTab(otherNodeSet, thisSpeciesTab);

  return {
    timber: compareTimberLists(thisTimber, otherTimber),
    hardware: compareHardwareLists(
      thisNodeSet.materials.hardwareList,
      otherNodeSet.materials.hardwareList,
    ),
    decking: compareDeckingItems(
      getStructureDeckingItems(thisNodeSet.materials),
      getStructureDeckingItems(otherNodeSet.materials),
    ),
    postBases: comparePostBasesItems(
      getStructurePostBasesItems(thisNodeSet.materials),
      getStructurePostBasesItems(otherNodeSet.materials),
    ),
    fields: {
      ...compareDesignFields(thisNodeSet.design, otherNodeSet.design),
      ...compareInstallFields(thisNodeSet.install, otherNodeSet.install),
      ...compareTravelFields(thisNodeSet.travel, otherNodeSet.travel),
    },
  };
}

export function formatStructureLineCompareTitle(entry: StructureLineCompareEntry): string {
  if (entry.mark === 'onlyThis') return 'Only on this structure';
  if (!entry.diffs.length) return 'Different from the other structure';
  return entry.diffs
    .map(diff => `${diff.field} ${diff.thisValue} vs ${diff.otherValue}`)
    .join(', ');
}

export function structureLineCompareRowClass(
  entry: StructureLineCompareEntry | undefined,
  prefix: 'timber' | 'hardware',
): string {
  if (!entry) return '';
  return entry.mark === 'changed' ? `${prefix}-row-compare-changed` : `${prefix}-row-compare-only`;
}

export function structureCompareRowProps(
  marks: StructureLineCompareMap | undefined,
  key: string,
): { className?: string; title?: string } {
  const entry = marks?.[key];
  if (!entry) return {};
  return {
    className: structureLineCompareRowClass(entry, 'hardware'),
    title: formatStructureLineCompareTitle(entry),
  };
}
